import React, { useEffect, useState } from "react";
import { getSigners, sendInvite, sendReminder } from "../../api/adminApi";
import { useToast } from "../common/ToastProvider";
import EmptyStateCard from "../common/EmptyStateCard";
import { SkeletonTable } from "../common/SkeletonBlock";

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

export default function SignerList({
  documentId,
  refreshKey = 0,
  signersData = null,
  readOnly = false,
}) {
  const [signers, setSigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingInviteId, setSendingInviteId] = useState(null);
  const [sendingReminderId, setSendingReminderId] = useState(null);
  const toast = useToast();

  useEffect(() => {
    if (Array.isArray(signersData)) {
      setSigners(signersData);
      setLoading(false);
      return;
    }

    if (documentId) load();
  }, [documentId, refreshKey, signersData]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getSigners(documentId);
      setSigners(res.data);
    } catch (e) {
      toast.error("Failed to load signers.");
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (id) => {
    try {
      setSendingInviteId(id);
      await sendInvite(documentId, id);
      toast.success("Invite sent successfully.");
      if (!Array.isArray(signersData)) {
        load();
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Failed to send invite.",
      );
    } finally {
      setSendingInviteId(null);
    }
  };

  const handleReminder = async (id) => {
    try {
      setSendingReminderId(id);
      await sendReminder(documentId, id);
      toast.success("Reminder sent successfully.");
      if (!Array.isArray(signersData)) {
        load();
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Failed to send reminder.",
      );
    } finally {
      setSendingReminderId(null);
    }
  };

  if (loading) {
    return <SkeletonTable rows={4} cols={12} />;
  }

  return (
    <div className="card">
      {signers.length === 0 ? (
        <EmptyStateCard
          icon="✍️"
          title="No signers added yet"
          message="Add signers from the Overview tab before you place fields or send invites."
          compact
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Last Invite</th>
                <th>Invite Count</th>
                <th>Last Reminder</th>
                <th>Reminder Count</th>
                <th>First Viewed</th>
                <th>Last Active</th>
                <th>Views</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {signers.map((s) => {
                const locked =
                  readOnly || s.status === "SIGNED" || s.status === "REJECTED";

                return (
                  <tr key={s.id}>
                    <td>{s.signOrder}</td>
                    <td>{s.name}</td>
                    <td>{s.email}</td>
                    <td>
                      <span
                        className={`badge badge-${String(s.status).toLowerCase()}`}
                      >
                        {s.status}
                      </span>
                    </td>
                    <td>{formatDate(s.lastInviteSentAt)}</td>
                    <td>{s.inviteCount ?? 0}</td>
                    <td>{formatDate(s.lastReminderSentAt)}</td>
                    <td>{s.reminderCount ?? 0}</td>
                    <td>{formatDate(s.firstViewedAt)}</td>
                    <td>{formatDate(s.lastActiveAt)}</td>
                    <td>{s.viewCount ?? 0}</td>
                    <td>
                      <div className="inline-actions">
                        <button
                          className="btn btn-primary"
                          disabled={locked || sendingInviteId === s.id}
                          onClick={() => handleInvite(s.id)}
                        >
                          {sendingInviteId === s.id ? "..." : "Invite"}
                        </button>

                        <button
                          className="btn btn-light"
                          disabled={locked || sendingReminderId === s.id}
                          onClick={() => handleReminder(s.id)}
                        >
                          {sendingReminderId === s.id ? "..." : "Reminder"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
